import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGamesHeroHeight } from '../../components/banners/useGamesHeroHeight';
import { useHeaderHeight } from '../../components/headers/useHeaderHeight';
import { PRIMARY_HTML_ELEMENT_ID } from '../../config/constants';
import { useStores } from '../../stores/useStores';
import { scrollTo } from '../../utils/scrollTo';

export const useHomeContentLogic = () => {
  const [scrolledPercent, setScrolledPercent] = useState(0);
  const { gamesSearchStore, categoriesStore, uiStore } = useStores();
  const { gamesHeroHeight } = useGamesHeroHeight();
  const { headerHeight } = useHeaderHeight();

  const hideVisualEffects = useMemo(() => uiStore.openFullPageDialog !== null, [uiStore.openFullPageDialog]);

  const { searchTerm } = gamesSearchStore;
  const { currentId } = categoriesStore;

  const headerHeightForScroll = useMemo(() => {
    return gamesHeroHeight - headerHeight;
  }, [gamesHeroHeight, headerHeight]);

  const homeScrollListener = useCallback(() => {
    const scrolledElement = document.getElementById(PRIMARY_HTML_ELEMENT_ID);
    if (!scrolledElement) {
      return;
    }

    if (scrolledElement.scrollTop > 0) {
      document.body.classList.add('scrolled');
    } else {
      document.body.classList.remove('scrolled');
    }

    const scrollTop = scrolledElement.scrollTop;
    const scrollHeight = headerHeightForScroll;
    const scrolled = (scrollTop / scrollHeight) * 100;
    const roundedScrolledValue = Math.max(0, Math.min(100, Math.round(scrolled)));

    if (roundedScrolledValue >= 100) {
      document.body.classList.add('lobby_scroll_finished');
    } else {
      document.body.classList.remove('lobby_scroll_finished');
    }

    setScrolledPercent(roundedScrolledValue);
  }, [headerHeightForScroll]);

  useEffect(() => {
    document?.removeEventListener('scroll', homeScrollListener);

    document?.addEventListener('scroll', homeScrollListener);

    return () => {
      document?.removeEventListener('scroll', homeScrollListener);
      document.body.classList.remove('scrolled');
    };
  }, [homeScrollListener]);

  const resetScrolledGames = useCallback(() => {
    if (typeof headerHeightForScroll !== 'number') {
      return;
    }

    const scrolledElement = document.getElementById(PRIMARY_HTML_ELEMENT_ID);

    if (!scrolledElement) {
      return;
    }

    const yOffset = scrolledElement.scrollTop;

    const isHeaderScrolled = yOffset > headerHeightForScroll;

    if (!isHeaderScrolled) {
      return;
    }

    scrollTo(PRIMARY_HTML_ELEMENT_ID, headerHeightForScroll);
  }, [headerHeightForScroll]);

  useEffect(() => {
    resetScrolledGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentId]);

  return {
    headerHeightForScroll,
    headerHeight,
    scrolledPercent,
    hideVisualEffects,
  };
};
